import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardService, orderService, shopService } from '../services';
import AdCarousel from '../components/AdCarousel';
import AdUpload from '../components/AdUpload';

const ShopDashboard = () => {
  const navigate = useNavigate();
  const { logout, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard', 'orders', 'ads', 'profile'
  const [view, setView] = useState('daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shopId, setShopId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [shopDetails, setShopDetails] = useState(null);
  const [adImages, setAdImages] = useState([]);
  const [showAdUpload, setShowAdUpload] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    if (userProfile) {
      setShopId(userProfile.shopId);
    }
  }, [userProfile]);

  useEffect(() => {
    if (shopId) {
      loadDashboard();
      loadOrders();
      loadShopDetails();
      loadAds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, view, showAllOrders]);

  const loadDashboard = async () => {
    if (!shopId) return;

    setLoading(true);
    try {
      let response;
      if (view === 'daily') {
        response = await dashboardService.getDailyDashboard(shopId);
      } else if (view === 'monthly') {
        const now = new Date();
        response = await dashboardService.getMonthlyDashboard(
          shopId,
          now.getFullYear(),
          now.getMonth() + 1
        );
      } else {
        response = await dashboardService.getYearlyDashboard(shopId, new Date().getFullYear());
      }

      if (response.success) {
        setData(response);
      }
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    if (!shopId) return;

    setLoadingOrders(true);
    try {
      const limit = showAllOrders ? 100 : 10;
      const response = await orderService.getShopOrders(shopId, { limit });
      if (response.success) {
        setOrders(response.orders || []);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setLoadingOrders(false);
    }
  };

  const loadShopDetails = async () => {
    if (!shopId) return;

    try {
      const response = await shopService.getShopById(shopId);
      if (response.success) {
        setShopDetails(response.shop);
      }
    } catch (err) {
      console.error('Error loading shop details:', err);
    }
  };

  const loadAds = async () => {
    if (!shopId) return;

    try {
      const response = await shopService.getShopAds(shopId);
      if (response.success) {
        setAdImages(response.adImages || []);
      }
    } catch (err) {
      console.error('Error loading ads:', err);
    }
  };

  const handleAdUpload = async (file) => {
    try {
      const response = await shopService.uploadAdImage(shopId, file);
      if (response.success) {
        await loadAds();
        setShowAdUpload(false);
        alert('Ad uploaded successfully!');
      }
    } catch (err) {
      console.error('Error uploading ad:', err);
      alert('Failed to upload ad');
    }
  };

  const handleAdDelete = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;

    try {
      const response = await shopService.deleteAdImage(shopId, imageUrl);
      if (response.success) {
        await loadAds();
        alert('Ad deleted successfully!');
      }
    } catch (err) {
      console.error('Error deleting ad:', err);
      alert('Failed to delete ad');
    }
  };

  if (!shopId) {
    return <div style={{ padding: '20px' }}>No shop assigned to this account</div>;
  }

  const tabStyle = (tab) => ({
    flex: 1,
    padding: '12px 20px',
    backgroundColor: activeTab === tab ? '#2196F3' : '#f5f5f5',
    color: activeTab === tab ? 'white' : '#333',
    border: 'none',
    borderBottom: activeTab === tab ? '3px solid #1976D2' : '3px solid transparent',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: activeTab === tab ? 'bold' : 'normal',
    transition: 'all 0.3s'
  });

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h1 style={{ margin: 0 }}>{shopDetails?.shopName || 'Shop Dashboard'}</h1>
            <button
              onClick={handleLogout}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Logout
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #ddd' }}>
            <button onClick={() => setActiveTab('dashboard')} style={tabStyle('dashboard')}>
              📊 Dashboard
            </button>
            <button onClick={() => setActiveTab('orders')} style={tabStyle('orders')}>
              📦 Orders
            </button>
            <button onClick={() => setActiveTab('ads')} style={tabStyle('ads')}>
              🎨 Manage Ads
            </button>
            <button onClick={() => setActiveTab('profile')} style={tabStyle('profile')}>
              🏪 Profile
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '0 20px 20px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            {/* View Selector */}
            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setView('daily')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: view === 'daily' ? '#2196F3' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Daily
              </button>
              <button
                onClick={() => setView('monthly')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: view === 'monthly' ? '#2196F3' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setView('yearly')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: view === 'yearly' ? '#2196F3' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Yearly
              </button>
            </div>

            {loading ? (
              <div>Loading...</div>
            ) : data ? (
              <div>
                {/* Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                  <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ color: '#4CAF50', margin: '0 0 10px 0' }}>Total Sales</h3>
                    <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>₹{data.summary.totalSales.toFixed(2)}</p>
                  </div>
                  <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ color: '#2196F3', margin: '0 0 10px 0' }}>Customers</h3>
                    <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{data.summary.customerCount}</p>
                  </div>
                  <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ color: '#FF9800', margin: '0 0 10px 0' }}>Avg Bill</h3>
                    <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>₹{data.summary.averageBillValue.toFixed(2)}</p>
                  </div>
                </div>

                {/* Product Consumption */}
                {(data.productConsumption || data.topProducts) && (
                  <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
                    <h2>Product Consumption</h2>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Product</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Quantity</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.productConsumption || data.topProducts).map((product, index) => (
                          <tr key={index}>
                            <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{product.productName}</td>
                            <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>
                              {product.totalQuantity} {product.unit}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>
                              ₹{product.totalRevenue.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <div>No data available</div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>Orders</h2>
              <button
                onClick={() => setShowAllOrders(!showAllOrders)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#9C27B0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {showAllOrders ? 'Show Recent Only' : 'View All Orders'}
              </button>
            </div>

            {loadingOrders ? (
              <div>Loading orders...</div>
            ) : orders.length > 0 ? (
              <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Date & Time</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Customer</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Items</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Total</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.orderId}>
                        <td style={{ padding: '12px', borderBottom: '1px solid #eee', fontSize: '14px' }}>
                          {new Date(order.orderDate).toLocaleString('en-IN', {
                            dateStyle: 'short',
                            timeStyle: 'short'
                          })}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                          <div style={{ fontWeight: '500' }}>{order.customerName || 'N/A'}</div>
                          <small style={{ color: '#666' }}>{order.customerMobile || 'N/A'}</small>
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                          <div style={{ fontWeight: '500' }}>{order.orderItems?.length || 0} items</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {order.orderItems?.slice(0, 2).map(item => item.productName).join(', ')}
                            {order.orderItems?.length > 2 && '...'}
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>
                          ₹{order.totalAmount?.toFixed(2) || '0.00'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                          <button
                            onClick={() => navigate(`/order-detail/${order.orderId}`)}
                            style={{
                              padding: '6px 16px',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px' }}>
                No orders found
              </div>
            )}
          </div>
        )}

        {/* Manage Ads Tab */}
        {activeTab === 'ads' && (
          <div>
            <h2>Manage Ad Banners</h2>

            {/* Ad Preview */}
            {adImages.length > 0 && (
              <div style={{ marginBottom: '30px' }}>
                <h3>Customer View Preview</h3>
                <AdCarousel images={adImages} />
              </div>
            )}

            {/* Upload Section */}
            {showAdUpload ? (
              <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
                <AdUpload
                  shopId={shopId}
                  onUploadSuccess={handleAdUpload}
                  onCancel={() => setShowAdUpload(false)}
                />
              </div>
            ) : (
              <button
                onClick={() => setShowAdUpload(true)}
                style={{
                  padding: '15px 30px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  marginBottom: '20px',
                  fontWeight: 'bold'
                }}
              >
                + Upload New Ad
              </button>
            )}

            {/* Current Ads Grid */}
            {adImages.length > 0 ? (
              <div>
                <h3>Current Ads ({adImages.length})</h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '20px'
                }}>
                  {adImages.map((imageUrl, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <img
                        src={imageUrl}
                        alt={`Ad ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{ padding: '15px' }}>
                        <button
                          onClick={() => handleAdDelete(imageUrl)}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          🗑️ Delete Ad
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <p style={{ fontSize: '18px', color: '#666' }}>No ads uploaded yet</p>
                <p style={{ color: '#999' }}>Upload your first ad banner to promote sales and offers!</p>
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && shopDetails && (
          <div>
            <h2>Shop Profile</h2>
            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ color: '#2196F3', marginTop: 0 }}>{shopDetails.shopName}</h3>
              <p><strong>Owner:</strong> {shopDetails.ownerName}</p>
              <p><strong>Mobile:</strong> {shopDetails.mobile}</p>
              <p><strong>Address:</strong> {shopDetails.address}</p>
              <p>
                <strong>Status:</strong>{' '}
                <span style={{
                  color: shopDetails.isActive ? '#4CAF50' : '#f44336',
                  fontWeight: 'bold'
                }}>
                  {shopDetails.isActive ? 'Active' : 'Inactive'}
                </span>
              </p>

              <div style={{ marginTop: '30px', textAlign: 'center' }}>
                <h3>Shop QR Code</h3>
                <p style={{ fontSize: '14px', color: '#666' }}>Customers can scan this to place orders</p>
                <img
                  src={shopDetails.qrCodeUrl}
                  alt="Shop QR Code"
                  style={{
                    maxWidth: '300px',
                    width: '100%',
                    border: '2px solid #ccc',
                    padding: '10px',
                    borderRadius: '8px',
                    marginTop: '10px'
                  }}
                />
                <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                  <a
                    href={shopDetails.qrCodeUrl}
                    download={`${shopDetails.shopName}-qr.png`}
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '4px'
                    }}
                  >
                    Download QR Code
                  </a>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(shopDetails.orderUrl);
                      alert('Order URL copied to clipboard!');
                    }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#FF9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Copy Order URL
                  </button>
                </div>
              </div>

              <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px'
              }}>
                <strong>Order URL:</strong>
                <div style={{ fontSize: '14px', wordBreak: 'break-all', marginTop: '5px' }}>
                  {shopDetails.orderUrl}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopDashboard;
